import type { Json } from "../../services/backend/database.types.js";
import type { AdminContentEditorValue } from "./adminContentEditor.js";

type Obj = Record<string, Json | undefined>;
type Section = Obj & { id?: Json; type?: Json };

interface TravelEditorValue {
    catalog: Obj;
    document: Obj;
    original: Obj;
    sections: Section[];
    sectionKey: "sections" | "miniLessons";
}

function asObject(value: Json | undefined): Obj {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value : {};
}

function readAdminTravelEditor(editor: AdminContentEditorValue): TravelEditorValue {
    let original: Obj = {};
    try { original = asObject(JSON.parse(editor.payloadText) as Json); }
    catch { /* The advanced JSON remains editable until it is valid. */ }
    const document = asObject(original.document);
    const sectionKey = Array.isArray(document.miniLessons)
        ? "miniLessons" : "sections";
    const sections = Array.isArray(document[sectionKey])
        ? (document[sectionKey] as Json[]).map(value => asObject(value))
        : [];
    return {
        catalog: asObject(original.catalog), document, original,
        sections, sectionKey
    };
}

function updateAdminTravelEditor(
    editor: AdminContentEditorValue,
    travel: TravelEditorValue
): AdminContentEditorValue {
    const catalog = {
        ...travel.catalog,
        id: editor.contentKey,
        title: String(travel.catalog.title ?? travel.document.title ?? ""),
        title_fa: travel.catalog.title_fa ?? travel.document.title_fa
    };
    const document = {
        ...travel.document,
        id: editor.contentKey,
        [travel.sectionKey]: travel.sections
    };
    return {
        ...editor,
        titleFr: String(catalog.title),
        titleFa: String(catalog.title_fa ?? ""),
        payloadText: JSON.stringify({
            ...travel.original, catalog, document
        }, null, 2)
    };
}

function validateAdminTravelEditor(
    travel: TravelEditorValue,
    contentKey: string
): string[] {
    const errors: string[] = [];
    if (!contentKey.trim()) errors.push("Identifiant requis.");
    if (!String(travel.catalog.title ?? "").trim()) errors.push("Titre du catalogue requis.");
    if (!String(travel.document.title ?? "").trim()) errors.push("Titre de la leçon requis.");
    if (travel.sections.length === 0) errors.push("Ajoutez au moins une section.");
    const ids = new Set<string>();
    for (const [index, section] of travel.sections.entries()) {
        const id = String(section.id ?? "").trim();
        const type = String(section.type ?? "");
        if (!id || ids.has(id)) errors.push(`Section ${index + 1} : identifiant manquant ou répété.`);
        ids.add(id);
        if (!["lesson", "vocab", "tips", "exercise"].includes(type)) {
            errors.push(`Section ${index + 1} : type inconnu.`);
        }
        if (type === "vocab" && !Array.isArray(section.words)) {
            errors.push(`Section ${index + 1} : mots requis.`);
        }
        if (type === "tips" && !Array.isArray(section.tips)) {
            errors.push(`Section ${index + 1} : conseils requis.`);
        }
        if (type === "exercise" && !Array.isArray(section.questions)) {
            errors.push(`Section ${index + 1} : questions requises.`);
        }
    }
    return errors;
}

export { asObject, readAdminTravelEditor, updateAdminTravelEditor, validateAdminTravelEditor };
export type { Obj, Section, TravelEditorValue };
